import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Expanded, InputSelectField, InputTextField, Row, Typography,
} from '@tempmee/react';
import { OutlineSearchIcon } from '@tempmee/react-icons';
import useDebounce from '../../../../hooks/useDebounce';
import {
  MEMBERSHIP_STATUSES,
  MEMBERSHIP_STATUS_LABELS,
} from '../../../../domain/Awards';

const ALL = '';

const USER_TYPE_OPTIONS = [
  { value: ALL, label: 'All' },
  { value: 'DNT', label: 'DNT' },
  { value: 'HYG', label: 'HYG' },
];

const STATUS_OPTIONS = [
  { value: ALL, label: 'All' },
  ...MEMBERSHIP_STATUSES.map((value) => ({
    value,
    label: MEMBERSHIP_STATUS_LABELS[value],
  })),
];

/**
 * Names an `InputSelectField` for assistive tech.
 *
 * The component spreads no rest props, so neither `id` nor `aria-label`
 * reaches its trigger, and its own `label` renders as an unassociated span —
 * a visible caption with no accessible name. `ref` is the one prop it does
 * forward, so the name is set through it. Delete this if the design system
 * grows an `aria-label` prop.
 */
const useAccessibleName = (label) => useCallback((node) => {
  if (node) node.setAttribute('aria-label', label);
}, [label]);

/** The roster's filter bar: search, two selects, and the filtered count. */
export const MembershipFilters = ({ filters, onFiltersChange, totalCount }) => {
  // Local state keeps typing responsive; only the debounced value reaches the
  // query key. useDebounce here is the repo's debounced-*callback* hook, so
  // there is no debounced value to read directly.
  const [searchInput, setSearchInput] = useState(filters.search);

  // Functional update, not { ...filters, search }: the debounce fires up to
  // 400 ms after the keystroke, so its captured `filters` can be stale by then
  // (e.g. a Status change applied in between). Merging into the latest state
  // keeps the concurrent change instead of clobbering it back to its old value.
  const promoteSearch = useDebounce(
    (value) => onFiltersChange((prev) => ({ ...prev, search: value })),
    400,
  );

  const handleSearch = (value) => {
    setSearchInput(value);
    promoteSearch(value);
  };

  const userTypeRef = useAccessibleName('User Type');
  const statusRef = useAccessibleName('Status');

  return (
    <Row gap={3} align="end" wrap="wrap">
      <Box width="280px">
        <InputTextField
          aria-label="Search users"
          placeholder="Search users"
          leftIcon={<OutlineSearchIcon aria-hidden className="size-5" />}
          value={searchInput}
          onChange={(event) => handleSearch(event.target.value)}
        />
      </Box>

      <Box width="180px">
        <InputSelectField
          ref={userTypeRef}
          label="User Type"
          options={USER_TYPE_OPTIONS}
          value={filters.userType}
          onChange={(userType) => onFiltersChange((prev) => ({ ...prev, userType }))}
        />
      </Box>

      <Box width="180px">
        <InputSelectField
          ref={statusRef}
          label="Status"
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={(status) => onFiltersChange((prev) => ({ ...prev, status }))}
        />
      </Box>

      {/* Pushes the count to the trailing edge without a margin. */}
      <Expanded />

      {/* totalCount reflects the active filters, per the server contract. */}
      <Typography variant="body2" className="text-text-secondary">
        {totalCount === 1 ? '1 user' : `${totalCount} users`}
      </Typography>
    </Row>
  );
};

MembershipFilters.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string.isRequired,
    userType: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
};
